import React, { useRef, useState, useEffect, cloneElement } from 'react';
import * as S from './Tooltip.styles';

type TooltipProps = {
  content?: string;
  children: React.ReactElement;
  disabled?: boolean;
};

type Position = 'right-start' | 'top-end';

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  disabled,
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<Position>('right-start');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleUpdatePosition = () => {
    if (!visible || disabled || !wrapperRef.current || !tooltipRef.current)
      return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const spaceRight = window.innerWidth - wrapperRect.right;
    const spaceTop = wrapperRect.top;

    if (
      spaceRight < tooltipRect.width + 20 &&
      spaceTop > tooltipRect.height + 20
    ) {
      setPosition('top-end');
    } else {
      setPosition('right-start');
    }
  };

  useEffect(() => {
    handleUpdatePosition();
    window.addEventListener('resize', handleUpdatePosition);
    return () => window.removeEventListener('resize', handleUpdatePosition);
  }, [visible]);

  const trigger = cloneElement(children, {
    onFocus: () => setVisible(true),
    onBlur: () => setVisible(false),
  });

  if (disabled) return <S.Wrapper>{children}</S.Wrapper>;

  return (
    <S.Wrapper ref={wrapperRef}>
      {trigger}
      {visible && (
        <S.TooltipBox ref={tooltipRef} $position={position}>
          {content}
        </S.TooltipBox>
      )}
    </S.Wrapper>
  );
};
